/**
 * Admin, Approvals and Procurement nav structure.
 *
 * Admin is a single top-level menu of groups; each group is a flyout of pages.
 * A group (and Admin itself) is shown when the user can open at least one page
 * inside it — the same permission keys the routes already use.
 *
 * Pages that are not built yet (shifts, thresholds, sites, inspection setup, …)
 * are omitted so the menu never links to a 404.
 */

export type HasPermission = (key: string) => boolean;

export interface AdminNavItem {
  label: string;
  to: string;
  visible: (has: HasPermission) => boolean;
}

export interface AdminNavGroup {
  id: string;
  label: string;
  items: AdminNavItem[];
}

const anyOf =
  (...keys: string[]) =>
  (has: HasPermission): boolean =>
    keys.some((key) => has(key));

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    id: 'people-access',
    label: 'People & Access',
    items: [
      {
        label: 'Employees',
        to: '/users?tab=employees',
        visible: anyOf('user:create', 'employee:view'),
      },
      {
        label: 'Classifications & Rates',
        to: '/users?tab=classifications',
        // Matches the Classifications tab on UsersPage (explicit key or user-admin).
        visible: anyOf('classification:view', 'user:create', 'user:edit'),
      },
      {
        label: 'Roles & Permissions',
        to: '/users?tab=roles',
        visible: anyOf('role:view'),
      },
    ],
  },
  {
    id: 'work-activities',
    label: 'Work & Activities',
    items: [
      {
        label: 'Activities',
        to: '/activities',
        visible: anyOf('activity:create'),
      },
      {
        label: 'Standing Tasks',
        to: '/standing-tasks',
        visible: anyOf('standing:tasks_view'),
      },
    ],
  },
  {
    id: 'areas-assets',
    label: 'Areas & Assets',
    items: [
      {
        label: 'Areas',
        to: '/areas',
        visible: anyOf('area:service_view', 'area:foreman_view', 'area:contract_view'),
      },
      {
        label: 'Assets',
        to: '/assets',
        visible: anyOf('asset:view'),
      },
    ],
  },
  {
    id: 'fleet-equipment',
    label: 'Fleet & Equipment',
    items: [
      {
        label: 'Vehicles',
        to: '/vehicles',
        visible: anyOf('vehicle:view'),
      },
      {
        label: 'Vehicle Profiles',
        to: '/vehicle-profiles',
        visible: anyOf('vehicle:profile:manage', 'vehicle:edit'),
      },
      {
        label: 'Fuel',
        to: '/admin/fuel-management',
        visible: anyOf('fuel_management:view'),
      },
    ],
  },
  {
    id: 'procurement-materials',
    label: 'Procurement & Materials',
    items: [
      {
        label: 'Vendors',
        to: '/vendors',
        // heMaster:vendor_read is picker-only (HE form) and must not open this tab.
        visible: anyOf('heMaster:vendor_view'),
      },
      // Purchase Orders moved to its own top-level Procurement nav item, next to
      // Approvals — see isProcurementVisible below.
    ],
  },
  {
    id: 'reference-data',
    label: 'Reference Data',
    items: [
      {
        label: 'Animal Types',
        to: '/admin/data-sources/animal-types',
        visible: anyOf('data_sources:view'),
      },
      {
        label: 'CHRIS Properties',
        to: '/admin/data-sources/chris-properties',
        visible: anyOf('data_sources:view'),
      },
      {
        label: 'WARS',
        to: '/admin/data-sources/wars',
        visible: anyOf('data_sources:view'),
      },
    ],
  },
];

/**
 * Top-level Approvals menu (sits next to Forms). Vendor Admin lives here.
 * Navbar awaiting-counts are loaded lazily per `to` in awaitingApprovalsCount.ts.
 */
export const APPROVALS_NAV_ITEMS: AdminNavItem[] = [
  {
    label: 'Vendors',
    to: '/he-admin',
    visible: anyOf('heMaster:vendor_approve'),
  },
];

export function visibleGroupItems(group: AdminNavGroup, has: HasPermission): AdminNavItem[] {
  return group.items.filter((item) => item.visible(has));
}

export function isGroupVisible(group: AdminNavGroup, has: HasPermission): boolean {
  return visibleGroupItems(group, has).length > 0;
}

export function isAdminMenuVisible(has: HasPermission): boolean {
  return ADMIN_NAV_GROUPS.some((group) => isGroupVisible(group, has));
}

export function visibleApprovalsItems(has: HasPermission): AdminNavItem[] {
  return APPROVALS_NAV_ITEMS.filter((item) => item.visible(has));
}

export function isApprovalsMenuVisible(has: HasPermission): boolean {
  return visibleApprovalsItems(has).length > 0;
}

/**
 * Top-level Procurement link, next to Approvals. A direct link rather than a
 * dropdown — Purchase Orders is its only destination today. If a second
 * procurement page shows up, promote this to a group the same shape as
 * APPROVALS_NAV_ITEMS rather than bolting a second link on beside it.
 */
export function isProcurementVisible(has: HasPermission): boolean {
  return has('po:view');
}
